import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CREATE_COUNTRY, UPDATE_COUNTRY } from '../../constants';
import { createCountry, getCountry, updateCountry } from '../../store/actions/country';
import Button from '../FormComponents/Button';
import TextField from '../FormComponents/TextField';
import NoData from '../NoData/NoData';
import ToastMessage from '../ToastMessage/ToastMessage';

interface CountryFormProps {
  type: string;
}

function CountryForm(props: CountryFormProps) {
  const { type } = props;
  const { countryId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [countryName, setCountryName] = useState('');
  const [countryRegion, setCountryRegion] = useState('');
  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [notFound, setNotFound] = useState(false);

  const getData = () => {
    dispatch(
      getCountry(countryId, res => {
        if (!res) {
          setNotFound(true);
        } else {
          setCountryName(res.name);
          setCountryRegion(res.region);
        }
      }),
    );
  };

  useEffect(() => {
    if (type === UPDATE_COUNTRY) {
      if (location?.state?.message) {
        setToastType('success');
        setToast(location?.state?.message);
        window.history.replaceState({}, '');
      }
      getData();
    }
  }, []);

  const handleFormClick = () => {
    setFormClicked(true);
    if (!countryName || !countryRegion) {
      return;
    }
    setLoading(true);
    if (type === CREATE_COUNTRY) {
      dispatch(
        createCountry(
          {
            name: countryName,
            region: countryRegion,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              navigate('/countries', {
                state: {
                  message: 'Country is created.',
                },
              });
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    } else {
      dispatch(
        updateCountry(
          {
            id: countryId,
            name: countryName,
            region: countryRegion,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              navigate('/countries', {
                state: {
                  message: 'Country is updated.',
                },
              });
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    }
  };

  if (type === UPDATE_COUNTRY && notFound) {
    return <NoData message='No country found' />;
  }

  return (
    <>
      <Box>
        <Typography variant='h5'>Country {type === CREATE_COUNTRY ? 'creation' : 'details'}</Typography>
        <Box display='flex' gap={4} marginTop={2}>
          <TextField
            label='Name'
            id='name'
            variant='outlined'
            sx={{ width: '30%' }}
            value={countryName}
            onChange={e => setCountryName(e.target.value)}
            required
            error={formClicked && !countryName}
          />
          <TextField
            label='Region'
            id='region'
            variant='outlined'
            sx={{ width: '30%' }}
            value={countryRegion}
            onChange={e => setCountryRegion(e.target.value)}
            required
            error={formClicked && !countryRegion}
          />
        </Box>
        <Box className='submit-button'>
          <Button text={type === CREATE_COUNTRY ? 'Create' : 'Update'} onClick={handleFormClick} loading={loading} />
        </Box>
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default CountryForm;
